/**
 * The main-app assembler for the three-state resolution model — the analog of the
 * studio's server-side `src/core/flowState.ts::buildStateContext`, adapted to the
 * React-SPA data seam: instead of SQLite reads it takes the batched hook data
 * (the read-model + statements + article-states + condition-fields) and builds
 * the pure `StateContext` that `cellState.ts` classifies. Pure + client-safe.
 *
 * Everything degrades gracefully: an unresolved supplier / legacy declaration
 * yields empty R2 maps and the classifier falls back per Seam 2 (the tier-1
 * auto-confirm). Article-keyed maps are in the PUBLIC article_number space (the
 * read-model translated the pipeline's internal ids at the API boundary).
 */
import type {
  ArticleState,
  ConditionFieldRow,
  ConditionFillFinding,
  DeclarationReadModel,
  PersistedOverrideNote,
  StatementCondition,
  StatementGroup,
  StatementGroupEntry,
} from "../../../../api/types"
import {
  deriveWorklist,
  scopeKey,
  unknownEditionResolved,
  type FrameworkFacts,
  type RequiredGap,
  type StateContext,
  type StatementFacts,
  type WorklistIssue,
} from "./cellState"
import { resolveFindingVerdict, verdictNeedsConditionData } from "./cellVerdict"
import {
  casArticles,
  readBindings,
  rowsAt,
  verdictBasisFilled,
} from "./conditionRows"
import { conditionSpec, verifiedKey } from "./conditions"
import { memberOf } from "./memberSets"
import {
  applyOverrides,
  cellStoreAddress,
  overrideDiverged,
  statementStoreAddress,
  type OverrideNote,
  type OverrideView,
} from "./overrides"
import { type CollisionItem } from "./statementTitle"
import type { Verdict } from "./studioTypes"

export interface FlowInputs {
  /** interface.statement_groups bridged to staged rows (useDeclarationStatements). */
  statements: StatementGroup[]
  /** The orion R2 read-model (useDeclarationReadModel) — snake-cased, verbatim. */
  readModel: DeclarationReadModel
  /** The supplier pool's current master state (useDeclarationArticleStates().articles). */
  articleStates: ArticleState[]
  /** The declaration's staged verify-stage rows (useDeclarationConditionFields().rows). */
  conditionRows: ConditionFieldRow[]
  /** Whether a framework has a reference-list catalog — gates the step-1
   *  unresolved-list-version needs-verification state. Sourced by the caller (no
   *  silent default, so the decision is explicit). */
  frameworkHasCatalog: (framework: string) => boolean
  /**
   * Step-2 applied marks per statement (`fw:groupIndex`) — the reviewer pressed
   * Apply. Optional: it OVERLAYS the persisted signal derived below, so a screen
   * can report an apply optimistically before the refetch lands.
   */
  appliedScopes?: Record<string, boolean>
  /**
   * Step-2 selections the reviewer has moved off their applied (or seeded)
   * baseline, keyed `fw:groupIndex` — the edited-and-unsigned yellow. Optional,
   * and nothing passes it yet: only the screen that owns the live selection can
   * see one, and screen B classifies its own card directly. Every other surface
   * reads persisted state, where a tick has left no trace at all.
   */
  divergedScopes?: Record<string, boolean>
  /** Guided-resolution ready marks (issueId → item ledger). Default {}. */
  pendingIssues?: Record<string, string[]>
}

const asVerdict = (v: string | null | undefined): Verdict | null =>
  v ? (v as Verdict) : null

/**
 * THE ONE RULE FOR GREEN, at the statement unit (§6 / I.1).
 *
 * Compare the identity ON SCREEN — the reviewer's `verdictOverride` where there is
 * one, the extraction's own verdict where there is not, and the effective list
 * version — against THE SIGNATURE if a human ever confirmed this statement
 * (`confirmedValues`), and against THE RUN'S OWN ANSWER (`proposed`) if not.
 *
 * The fallback to the proposal is deliberate and it is a reversal: a statement with
 * no confirmation record used to classify exactly as before. That was safe only while
 * every edit recomputed immediately; once the recompute waits for a signature it
 * becomes the green-and-stale case §6 forbids, and existing rows holding old unsigned
 * edits gain a yellow — the correction, not a regression (concept §7).
 *
 * A blank and an absent value are the same state on both sides, so a list version
 * cleared to "" does not read as a change from null. Kept byte-faithful to the Python
 * twin's `statement_diverged`.
 *
 * A STATEMENT THAT NAMES NO EDITION IS ANSWERED BY THE EDITION THE RUN SELECTED
 * (`selectedEdition`, the framework's edition axis). A blank list version therefore
 * reads as that edition on BOTH sides, so choosing exactly the edition the card
 * already uses is no divergence; any other edition still is. With no axis
 * (`selectedEdition` null) a blank stays blank.
 *
 * NEITHER BASELINE ON THE WIRE ⇒ NOT DIVERGED. A read model older than the signature
 * store sends no `proposed` at all, and "we cannot tell" must not read as "edited" —
 * that would turn every statement of every legacy declaration yellow at once, which is
 * a lie about what a human did. The honest blank is the house rule everywhere else in
 * this derivation and it is the rule here.
 */
export function statementDiverged(
  g: StatementGroupEntry,
  selectedEdition?: string | null,
): boolean {
  const norm = (v: string | null | undefined): string | null => v || null
  const edition = (v: string | null | undefined): string | null =>
    norm(v) ?? norm(selectedEdition)
  const baseline = g.confirmedValues ?? g.proposed
  if (!baseline) return false
  return (
    norm(g.verdictOverride ?? g.values.verdict) !== norm(baseline.verdict) ||
    edition(g.values.listVersion) !== edition(baseline.listVersion)
  )
}

/** The pipeline gaps no typed value can close: connecting a substance is M3's
 *  work, authoring a `result_spec` is the CMS's. A finding naming one of these
 *  stands until a recompute clears it. */
const UNHEALABLE_MISSING = new Set(["<unresolved-substance>", "<result_spec>"])

/** M6's "this disclosure names no substance at all" (reach-svhc). No typed
 *  value closes it; naming the slot does (the substance pick, TUR-1725): healed
 *  on a cell the read model's `named_slots` lists (`namedSlotCells`). */
const NO_SUBSTANCE_MISSING = "<no-substance-declared>"

/** The read model's `named_slots` (framework -> bare cas-or-id -> article
 *  numbers) as the cell keys "framework:articleNumber" whose slot a reviewer
 *  named.
 *
 *  Twin: `named_slot_keys` in backend mc_declaration_cell_state.py — keep in step. */
export function namedSlotCells(
  namedSlots: DeclarationReadModel["named_slots"],
): Set<string> {
  const out = new Set<string>()
  for (const [framework, byBare] of Object.entries(namedSlots ?? {}))
    for (const numbers of Object.values(byBare ?? {}))
      for (const n of numbers ?? []) out.add(`${framework}:${n}`)
  return out
}

/**
 * Has the reviewer HEALED this published M6 validation finding — does every
 * entry of its `missing` list now carry a non-blank value in the live condition
 * rows? The concentration is matched per the finding's substance, framework
 * fields per field key. The read model already translated the finding's
 * `substances:N` key to the bare cas-or-id the rows speak (the API boundary's
 * job); an untranslated key matches no row, so the finding stays unhealed. A
 * finding that names no substance (the no-disclosure case) accepts any
 * substance's row — a leniency the sign-off covers, since healing only
 * re-enables the verify gesture, never the green state itself.
 * `<no-substance-declared>` is healed once the cell's slot is named
 * (`namedCells`, the read model's `named_slots` as cell keys, TUR-1725).
 *
 * Twin: `_finding_healed` in backend mc_declaration_cell_state.py — keep in step.
 */
export function findingHealed(
  f: {
    framework: string
    article: string
    substance?: string | null
    missing?: string[]
  },
  rows: readonly ConditionFieldRow[],
  namedCells?: ReadonlySet<string>,
): boolean {
  const missing = f.missing ?? []
  if (missing.length === 0) return false
  const wanted = f.substance || null
  const filled = (fieldKey: string, framework: string | null) =>
    rowsAt(rows, f.article, fieldKey).some(
      (r) =>
        (framework === null || r.framework === framework) &&
        (wanted === null || r.substanceCas === wanted) &&
        (r.value ?? "").trim() !== "",
    )
  return missing.every((m) => {
    if (UNHEALABLE_MISSING.has(m)) return false
    if (m === NO_SUBSTANCE_MISSING)
      return namedCells?.has(`${f.framework}:${f.article}`) ?? false
    // The concentration is universal (framework-independent rows); everything
    // else is a framework field of the finding's own framework.
    if (m === "concentration") return filled("concentration", null)
    return filled(m, f.framework)
  })
}

/**
 * Per framework, the articles carrying a published `condition_fill.validation`
 * finding the approve gate does not skip outright: the same `data_gap` and
 * missing-address skips as the gate loop in `buildStateContext` (twin
 * `mc_declaration_cell_state.py::build_state_context`). A SUPERSET of the
 * gate's needs-input: the gate additionally drops findings whose cell no
 * longer needs condition data (a non-conditional verdict) and moves
 * unconfirmed readings to `conditionFindingUnconfirmed`.
 */
export function blockingFindingArticles(
  validation: readonly ConditionFillFinding[],
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>()
  for (const f of validation) {
    if (f.data_gap || !f.framework || !f.article) continue
    const set = out.get(f.framework)
    if (set) set.add(f.article)
    else out.set(f.framework, new Set([f.article]))
  }
  return out
}

/**
 * N9: the articles a scopeAll statement with no explicit subset and an EMPTY
 * M2 resolution can be edited on — its gate coverage set (`selectedArticles`,
 * the supplier pool `buildStateContext` expanded it to), in that order and
 * deduplicated, narrowed to the articles carrying a blocking validation
 * finding of its framework. Never the whole pool: a 10k-article pool would
 * render 10k editor rows (the deliberate "never the whole declaration pool"
 * rule of `StatementCard.resolvedArticles`). Shared by the editor
 * (`StatementCard.editorArticles`) and the live verdict-basis loop below, so
 * the two can never disagree about which articles are reachable.
 */
export function poolFallbackArticles(
  selectedArticles: readonly string[],
  framework: string,
  findingArticles: ReadonlyMap<string, ReadonlySet<string>>,
): string[] {
  const flagged = findingArticles.get(framework)
  if (!flagged) return []
  return [...new Set(selectedArticles)].filter((a) => flagged.has(a))
}

/** Build the pure StateContext from the batched hook data. */
export function buildStateContext(input: FlowInputs): StateContext {
  const { statements, readModel, articleStates, conditionRows } = input

  // The whole supplier pool's article numbers — a scopeAll statement's effective
  // scope when it carries no explicit per-statement subset and M2 resolved none.
  const poolNumbers = articleStates.map((a) => a.articleNumber)

  // THE REFERENCE-LIST READING, per framework. The read model's `editions` axis
  // still answers two things nothing else can: WHICH frameworks have a catalogue
  // to disagree about (an axis entry exists only for those), and which groups
  // named an edition we do not hold (`cites_unknown_edition` — extraction nulled
  // the id, so the statements themselves cannot say it). But WHAT the statements
  // cite is read off the statements: the conflict is a property of what they SAY,
  // counted over the NON-IGNORED ones, so the two gestures the cards already have
  // settle it at view time — ignore the odd statement out, or align its list.
  // The run's own gate output (`excluded`) is deliberately not read any more: it
  // could only be cleared by re-running the gate from a card of its own, and that
  // card is gone ("no card may be unbound from a statement").
  const unknownGroups: Record<string, Set<number>> = {}
  for (const [fw, axis] of Object.entries(readModel.editions ?? {})) {
    unknownGroups[fw] = new Set(
      (axis?.cites_unknown_edition ?? [])
        .filter((u) => u.group_index != null)
        .map((u) => u.group_index),
    )
  }
  // Per framework with an axis entry, the reference lists its enabled statements
  // cite — in first-cited group order, so both twins read the same list. An
  // IGNORED (rejected) statement's citation is nobody's side of a disagreement,
  // and a statement naming no edition appears nowhere: silence is answered by
  // the edition the run selected, not disputed.
  const frameworks: FrameworkFacts[] = []
  for (const fw of Object.keys(readModel.editions ?? {})) {
    const cited: { listVersion: string; groupIndexes: number[] }[] = []
    for (const group of statements) {
      if (group.frameworkSlug !== fw) continue
      group.groups.forEach((g, gi) => {
        if (g.statementStatus === "rejected") return
        const lv = g.values.listVersion || null
        if (!lv) return
        const entry = cited.find((c) => c.listVersion === lv)
        if (entry) entry.groupIndexes.push(gi)
        else cited.push({ listVersion: lv, groupIndexes: [gi] })
      })
    }
    frameworks.push({ framework: fw, cited })
  }
  const conflictedFrameworks = new Set(
    frameworks.filter((f) => f.cited.length > 1).map((f) => f.framework),
  )

  // R-3 — the articles the binder's chain could not explain, counted PER
  // STATEMENT. The wire keys them by cell (`fw:articleNumber`) and each entry
  // names the covering statement's `group_index`, so the statement's count is the
  // number of entries under its framework that name it. Absent field → {}, which
  // is the pre-binder reading (no proofs published, nothing to be unexplained).
  const unexplainedScope: Record<string, number> = {}
  for (const [cell, gaps] of Object.entries(
    readModel.unexplained_articles ?? {},
  )) {
    const fw = cell.slice(0, cell.indexOf(":"))
    if (!fw) continue
    for (const gap of gaps) {
      // The wire may carry a null index (the binder knew the cell but not which
      // statement); an unattributable gap belongs to no statement's count.
      if (typeof gap.group_index !== "number") continue
      const key = scopeKey(fw, gap.group_index)
      unexplainedScope[key] = (unexplainedScope[key] ?? 0) + 1
    }
  }

  const facts: StatementFacts[] = []
  // N9: scopeAll statements with no explicit subset whose M2 resolution is
  // EMPTY, i.e. exactly those `selectedArticles` expands to the whole pool.
  // The live verdict-basis loop below narrows them to `poolFallbackArticles`,
  // the same set the editor offers (`StatementCard.editorArticles`).
  const poolFallbackStatementIds = new Set<string>()
  // THE APPLY MARK (§3 · the step-2 confirmation record). A statement with
  // `scope_all = false` carries a persisted per-statement subset, which is
  // exactly what pressing Apply writes (PUT /statements/{id}/scope) — so it IS
  // the confirmation record for the scope axis, and the derivation must see it
  // or a scope the reviewer applied would stay yellow on every other surface.
  // Screen B already read the same signal locally; this lifts it into the ONE
  // derivation so mission control, the worklist and the step agree.
  const applied: Record<string, boolean> = {}
  // Each statement's OWN extraction disclosures (`values.conditions`), stashed
  // here, by the same `scopeKey` the fact carries, purely so the live
  // verdict-basis check below (which runs after `facts` exists) can find a
  // statement's disclosed substances without re-walking `statements`.
  const conditionsByStatement = new Map<string, StatementCondition[]>()
  for (const group of statements) {
    const fw = group.frameworkSlug
    group.groups.forEach((g, gi) => {
      if (!g.statementId) return // no staged row → not an addressable issue
      conditionsByStatement.set(scopeKey(fw, gi), g.values.conditions)
      const resolved =
        readModel.scope_resolutions[`${fw}:${gi}`]?.article_numbers ?? []
      const selectedArticles = g.articleNumbers.length
        ? g.articleNumbers
        : g.scopeAll
          ? resolved.length
            ? resolved
            : poolNumbers
          : resolved
      if (!g.scopeAll) applied[scopeKey(fw, gi)] = true
      if (g.scopeAll && !g.articleNumbers.length && !resolved.length)
        poolFallbackStatementIds.add(g.statementId)
      facts.push({
        id: g.statementId,
        framework: fw,
        groupIndex: gi,
        verdict: asVerdict(g.verdictOverride ?? g.values.verdict),
        selectedArticles,
        scopeAll: g.scopeAll === true,
        listVersion: g.values.listVersion,
        frameworkHasCatalog: input.frameworkHasCatalog(fw),
        verdictOverridden: g.verdictOverride != null,
        confirmed: g.confirmedValues != null,
        diverged: statementDiverged(g, readModel.editions?.[fw]?.selected),
        // Derived SERVER-SIDE (an active note of that kind AND a verdict still
        // `unknown`) and read here, never re-joined from the note store — so the
        // browser and the approve gate cannot disagree about who is proceeding.
        proceedUnknown: g.proceedUnknown === true,
        // The CONFLICT is a fact about the framework, held identically on every
        // statement of it — including the ones citing the majority edition, whose
        // identity is equally unsignable until the dispute is settled.
        listConflict: conflictedFrameworks.has(fw),
        // Named an edition our catalogue does not hold AND still unresolved — the
        // SAME predicate the approve() gate's MISSING_LIST_VERSION check uses
        // (`unknownEditionResolved`), so a citation the reviewer closed there
        // (confirm_edition, or a per-statement listVersion) cannot still raise a
        // worklist issue here.
        citesUnknownEdition:
          unknownGroups[fw]?.has(gi) === true &&
          !unknownEditionResolved({
            chosen: readModel.editions?.[fw]?.chosen,
            listVersion: g.values.listVersion,
          }),
      })
    })
  }

  // M5 per-cell verdicts (cast the published code to Verdict).
  const cellVerdicts: StateContext["cellVerdicts"] = {}
  for (const [k, v] of Object.entries(readModel.article_verdicts)) {
    if (v?.verdict)
      cellVerdicts[k] = { verdict: v.verdict as Verdict, tier: v.tier }
  }

  const autoVerify = readModel.auto_verify ?? {}
  const hasAutoVerify = Object.keys(autoVerify).length > 0

  // Human sign-off marks from the staged `__verified__` rows.
  const verified: Record<string, boolean> = {}
  for (const row of conditionRows) {
    if (row.fieldKey === "__verified__" && row.framework) {
      verified[verifiedKey(row.framework, row.articleNumber)] =
        row.value !== "false"
    }
  }

  // needs-input cells from the published M6 validation — LESS the findings the
  // reviewer has since HEALED. The published validation is frozen at run time
  // (the record edit's cone is empty, and M6 reads only the machine's own
  // disclosures, never the human's saved values — see fill_m6), so without this
  // subtraction a flagged cell stays "needs input" forever, however completely
  // the reviewer fills the very fields the finding names — and the confirm
  // control that state blocks is the one gesture that could have moved it.
  // Healing only lifts red to yellow: the human signature stays the gate.
  // `facts` plus the staged row's own review status, which `StatementFacts` does
  // not carry (it is not a classifier input, and adding it would widen the shared
  // parity fixture's contract). A rejected statement is not a cover for the skip
  // below; see `resolveFindingVerdict`'s doc. Same status source the edition axis
  // above and `buildOverrideViews` already read.
  const rejectedStatementIds = new Set<string>()
  // Evidence that a HUMAN decided this statement's verdict: the dropdown writes
  // `verdictOverride`, and a `manuallyAdded` row is a reviewer's own statement
  // from end to end. Only such a statement may outrank M5 below; untouched
  // extraction must not (Codex review, finding 1). `manuallyAdded` is derived
  // server-side from the staged row's creator, so it survives a re-stage and an
  // `excluded` note superseding the `included` one.
  //
  // `confirmedValues` IS DELIBERATELY NOT HERE, though it is a human act on the
  // same statement. The step-1 signature is about the statement's IDENTITY
  // (verdict + list version), never about one cell's condition data, and
  // admitting it to tier 1 would CLOSE the finding — handing the cell straight
  // to M8's own auto decision with nobody having answered it. It also fires the
  // recompute cone, so M5's conditional reading after a confirm is NEWER than
  // the signature rather than stale behind it (the entire reason a save-only
  // verdict edit needs tier 1), and a later scope change never clears it, so a
  // signature would close findings on articles it never spoke about. A
  // signature leaves the finding SOFTENED, not closed; the cell's own
  // `__verified__` mark is what answers it.
  const reviewerDecidedIds = new Set<string>()
  for (const group of statements)
    for (const g of group.groups) {
      if (!g.statementId) continue
      if (g.statementStatus === "rejected")
        rejectedStatementIds.add(g.statementId)
      if (g.verdictOverride != null || g.manuallyAdded === true)
        reviewerDecidedIds.add(g.statementId)
    }
  // The cells whose nameless slot a reviewer named (the substance pick): M6's
  // `<no-substance-declared>` finding is healed there (`findingHealed`).
  const namedCells = namedSlotCells(readModel.named_slots)
  const findingCovers = facts.map((s) => ({
    ...s,
    rejected: rejectedStatementIds.has(s.id),
    reviewerDecided: reviewerDecidedIds.has(s.id),
  }))

  const requiredIncomplete = new Set<string>()
  const requiredGaps = new Map<string, RequiredGap[]>()
  const addRequiredGap = (key: string, gap: RequiredGap) => {
    requiredIncomplete.add(key)
    const list = requiredGaps.get(key)
    if (list) list.push(gap)
    else requiredGaps.set(key, [gap])
  }
  const conditionFindingUnconfirmed = new Set<string>()
  /**
   * DOES THIS CELL STILL HAVE SOMETHING TO FILL IN? (TUR-1211, sandbox
   * DEC-2026-0007, binding tester rule.) A finding whose cell IS covered — and
   * whose every LIVE cover reads a non-conditional verdict — names no datum the
   * reviewer can supply: the statements on screen all say there is no condition
   * to substantiate, and in the live case the document itself named no substance
   * either ("A10002 0.20" in an Annex III row). Only the run's own per-cell
   * verdict still disagrees, and that is a reading to CONFIRM, not a blank to
   * fill.
   *
   * It is a SOFTENING, never a closure: the finding stays open work (yellow, and
   * approve still refuses) until a human acts — signing the cell, or confirming
   * the covering statement, which closes the finding outright through tier 1
   * above. Closing it here on the statements' own untouched word is precisely
   * what tier 2 exists to prevent, and what an earlier draft of this fix was
   * rejected for.
   *
   * A cell with NO live cover (nothing covers it, or its only cover is rejected)
   * is deliberately excluded: there is no live reading to soften against, so the
   * run's own is the only one there is and the finding keeps its red.
   */
  const everyLiveCoverIsUnconditional = (
    framework: string,
    article: string,
  ): boolean => {
    // NO `s.verdict &&` HERE, deliberately (Copilot on #2337): a cover whose
    // verdict is still null is a live cover like any other, and it is not known
    // to be non-conditional — it is not known to be anything. Filtering it out
    // would let a MIXED cell (one compliant cover, one undecided one) soften on
    // the strength of the covers that happen to have an answer. The predicate
    // below says this by itself: `verdictNeedsConditionData(null)` is true, so
    // an undecided cover keeps the finding red until it has a verdict.
    const covers = findingCovers.filter(
      (s) =>
        !s.rejected &&
        s.framework === framework &&
        memberOf(s.selectedArticles, article),
    )
    return (
      covers.length > 0 &&
      covers.every((s) => !verdictNeedsConditionData(s.verdict))
    )
  }
  for (const f of readModel.condition_fill?.validation ?? []) {
    // A `data_gap` finding (2026-09-04 product decision, backend
    // fill_m6.validate_cells) is deliberately NOT a needs-input signal: the
    // verdict basis (exemption number / named substance) is already
    // established, only a completeness datum (the concentration) is still
    // open — a gap the reviewer can close without it ever having blocked
    // approval. Twin: the identical skip in the backend's
    // `mc_declaration_cell_state.py::build_state_context` — keep in step.
    if (f.data_gap) continue
    if (!f.framework || !f.article) continue
    // F1 (TUR-1211, the annex declaration regression): a finding must not outlive the
    // verdict that made condition data required at all. The reviewer moves the
    // statement (or M5 moves the cell) off `compliant_with_conditions` through
    // a save-only verdict edit, which recomputes nothing, so this published
    // validation stays frozen on a cell that now demands no condition datum
    // whatsoever (`rowComplete`'s own rule, `conditions.ts`). `cellInputs`
    // (`cellState.ts`) applies no verdict guard before turning this set into
    // needs-input, so THIS is where the verdict has to be read.
    //
    // `resolveFindingVerdict`, NOT `resolveCellVerdict`: the covering
    // statements' own verdict wins here and M5's per-cell entry is only the
    // fallback for a cell no statement covers. `article_verdicts` holds an
    // entry for EVERY covered cell (M5's own plus `synthesize_inherited_verdicts`
    // for the capped ones), and the verdict edit recomputes nothing, so under
    // the classifier's M5-first precedence a stale conditional cell verdict
    // would outlive every verdict a human can set and nothing would ever clear.
    // See that function's doc; the classifier's own precedence is untouched.
    //
    // A cell nothing covers and M5 never judged keeps its finding. Twin: the
    // identical skip in the backend's
    // `mc_declaration_cell_state.py::build_state_context`, keep in step.
    if (
      !verdictNeedsConditionData(
        resolveFindingVerdict(
          cellVerdicts,
          findingCovers,
          f.framework,
          f.article,
        ),
      )
    )
      continue
    if (!findingHealed(f, conditionRows, namedCells)) {
      // Still open work either way; `everyLiveCoverIsUnconditional` only decides
      // WHICH open state it is — a blank to fill, or a reading to confirm. A
      // finding that names no field has no blank to fill, so it is always a
      // reading to confirm (twin: `mc_declaration_cell_state.py`).
      if (
        !f.missing?.length ||
        everyLiveCoverIsUnconditional(f.framework, f.article)
      )
        conditionFindingUnconfirmed.add(`${f.framework}:${f.article}`)
      else
        addRequiredGap(`${f.framework}:${f.article}`, {
          substance: f.substance || null,
          fields: f.missing.filter(
            (field) =>
              !findingHealed(
                { ...f, missing: [field] },
                conditionRows,
                namedCells,
              ),
          ),
          anonymous: !f.substance,
        })
    }
  }

  // A LIVE verdict-basis gap the run never published a finding for: the
  // reviewer set (or edited) a `compliant_with_conditions` verdict after M6
  // ran, or M6 itself missed the cell; either way `condition_fill.validation`
  // above says nothing, yet the review-mask's own SubstanceEditor already
  // reads this gap live off the same rows (`verdictBasisFilled`, reused here
  // rather than reimplemented, see `conditionRows.ts`'s doc) and marks it
  // "Pflichtfeld offen". Without this, that cell reads "needs input" in the
  // editor and "complete" on the worklist that actually gates step 3's
  // confirm and the approve button (L3, live test wave 5).
  //
  // Scoped to the STATEMENT'S OWN disclosed substances (`values.conditions`),
  // never to every substanceCas the declaration's rows happen to mention: a
  // substance connected only under a different framework's statement on the
  // same article must not borrow that framework's exemption/EFUP requirement.
  //
  // Gated three ways, each removing a class of false positive:
  //   - only `compliant_with_conditions` demands condition data at all
  //     (`rowComplete`'s own rule, `conditions.ts`);
  //   - only a framework whose spec DEFINES a `requiredFor: "verdict"` field
  //     (rohs, china-rohs today): `verdictBasisFilled` reads as "nothing
  //     filled" with no such field to check, which would otherwise flag every
  //     compliant_with_conditions cell of every OTHER framework;
  //   - an M5 per-cell verdict, when published, overrides the statement's own
  //     (the same precedence `cellVerdict` in `cellState.ts` reads): a cell
  //     M5 moved off `compliant_with_conditions` is not this check's to
  //     raise. NEVER delete this gate to make the editor and the worklist
  //     read the same count for a cell M5 has moved on: `cellInputs`
  //     (`cellState.ts`) applies no verdict guard of its own before turning
  //     `requiredIncomplete` into `needs-input`, so THIS is the only place
  //     that precedence is enforced (TUR-1211 W5-L3b: an earlier draft of
  //     this fix deleted these two lines and was rejected in review for
  //     exactly that reason, pinned by
  //     `flowState.test.ts`'s "a cell M5 published as plain compliant..." test).
  //
  // A CAS with no extraction row at all under THIS statement (a reviewer
  // connected a substance the extraction never disclosed, or rebound an
  // extracted one to different articles than `c.articleNumbers` says) is
  // NOT out of scope: `casArticles` (`conditionRows.ts`) folds in this
  // statement's own reviewer bindings (`readBindings`, derived straight off
  // `conditionRows` below, the SAME staged rows every caller of
  // `buildStateContext` already fetches) for exactly this, the SAME rule the
  // review-mask's own `entryArticles`/`substanceEntries` already apply for
  // display. No wiring, no optional input a caller can forget to pass: every
  // caller of `buildStateContext`, v1 and v2 alike, gets this reading simply
  // by calling it. Previously this comment claimed the gap "surfaces via the
  // M6 `<unresolved-substance>` finding" instead; that is false whenever the
  // cell carries at least one OTHER genuinely-disclosed substance: the
  // backend's `fill_m6.validate_cells` only iterates the disclosures that
  // exist at run time on that cell (`cell_disclosures.get(cell)`), so a
  // substance the reviewer adds or rebinds afterward gets no finding of its
  // own. Only a cell with ZERO disclosures at all triggers M6's blanket
  // "no condition data" finding (the first loop above), which does land
  // here regardless.
  //
  // GATE 1 (TUR-1211 W5-L3c, live check failure, round 4): a statement with
  // ZERO extraction disclosures of ANY substance (`conditions.length === 0`,
  // exactly the live Phoenix Aussage 2 shape) must still be walked when it
  // has a reviewer binding of its own: `readBindings` is read BEFORE the
  // early-out below, and the early-out now checks BOTH sources empty, not
  // just the extraction's. A round-3 draft of this fix computed `bindings`
  // only after `if (!conditions?.length) continue` had already skipped the
  // statement, so a reviewer-added substance on an otherwise-empty statement
  // was silently unreachable, the exact bug this round's fixture caught.
  const findingArticlesByFramework = blockingFindingArticles(
    readModel.condition_fill?.validation ?? [],
  )
  for (const s of facts) {
    // A REJECTED statement is not a live claim, so its own disclosures must not
    // re-add a cell key here either (Codex review, finding 2): the set is
    // cell-keyed, so a live replacement statement over the same article would
    // inherit the blocker the rejected one raised. Same exclusion, same status
    // source, as the published-finding skip above. The backend twin runs no
    // live loop at all (it has no editor-side rows to read one from), so
    // excluding the rejection here moves this side TOWARDS the server's reading,
    // not away from it.
    if (rejectedStatementIds.has(s.id)) continue
    if (s.verdict !== "compliant_with_conditions") continue
    const spec = conditionSpec(s.framework)
    if (!spec.fields.some((field) => field.requiredFor === "verdict")) continue
    const conditions =
      conditionsByStatement.get(scopeKey(s.framework, s.groupIndex)) ?? []
    const bindings = readBindings(conditionRows, s.framework, s.id)
    if (conditions.length === 0 && bindings.size === 0) continue
    // N9 (server parity): a scopeAll statement with an EMPTY resolution covers
    // the whole supplier pool, so an unbound disclosure would spread over
    // every pool article here and block approval on articles the editor
    // (`StatementCard.editorArticles`) never offers: a dead end. The server
    // runs no live loop at all, so this loop only walks the articles the
    // editor can reach for such a statement: the ones a blocking published
    // finding already names. Every other statement is walked unchanged.
    const walked = poolFallbackStatementIds.has(s.id)
      ? poolFallbackArticles(
          s.selectedArticles,
          s.framework,
          findingArticlesByFramework,
        )
      : s.selectedArticles
    const articlesByCas = casArticles(conditions, walked, bindings)
    for (const art of walked) {
      const cellVerdict = cellVerdicts[`${s.framework}:${art}`]?.verdict
      if (cellVerdict && cellVerdict !== "compliant_with_conditions") continue
      for (const [cas, arts] of articlesByCas) {
        if (!memberOf(arts, art)) continue
        if (!verdictBasisFilled(spec, conditionRows, s.framework, art, cas))
          addRequiredGap(`${s.framework}:${art}`, {
            substance: cas,
            fields: spec.fields
              .filter((field) => field.requiredFor === "verdict")
              .map((field) => field.key),
            anonymous: false,
          })
      }
    }
  }

  // On-record (prev) verdicts per framework → articleNumber, from the master
  // article-states' per-framework statuses.
  const states: Record<string, Record<string, Verdict>> = {}
  for (const art of articleStates) {
    for (const c of art.compliance) {
      for (const [fw, status] of Object.entries(c.frameworkStatuses ?? {})) {
        ;(states[fw] ??= {})[art.articleNumber] = status as Verdict
      }
    }
  }

  // Adjudicator risky-conflict cells (A1).
  const risky: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(readModel.adjudication ?? {})) {
    if (v?.risky) risky[k] = true
  }

  return {
    statements: facts,
    pendingIssues: input.pendingIssues ?? {},
    cellVerdicts,
    autoVerify,
    hasAutoVerify,
    verified,
    requiredIncomplete,
    requiredGaps,
    conditionFindingUnconfirmed,
    states,
    scopeResolutions: readModel.scope_resolutions ?? {},
    applied: { ...applied, ...(input.appliedScopes ?? {}) },
    divergedScopes: input.divergedScopes ?? {},
    unexplainedScope,
    // D9 — the articles the document names that no approved scope covers. Stated
    // as {} rather than derived: the read model publishes no unassigned article
    // references, so there is no channel here to count them from. The classifier's
    // branch is live; only its input is missing, and an absent channel must read as
    // silence rather than be faked into a confident zero.
    uncoveredNamedScope: {},
    risky,
    // Cells the execution record holds a human's value on — the "changed" half of
    // "changed and not signed is yellow". Empty on a legacy read model.
    humanEdited: new Set(readModel.human_edited_cells ?? []),
    frameworks,
  }
}

/**
 * THE OVERRIDE BUCKET'S MEMBERS (§8 · U5) — derived membership joined to the
 * session's notes.
 *
 * Membership is never a stored flag of its own: `excluded` is the staged
 * statement's `rejected` review status, `suppressed` is an applied scope with
 * unmatched references still outstanding, and the two note-driven kinds are in
 * force exactly while their note stands AND their condition still holds (an
 * `ignored` article still in scope, a verdict still reading unknown). So
 * membership and the comment can never disagree about whether the disposition is
 * in force — there is one answer, and the note is joined to it at read time.
 *
 * `cells` is what the mission-control matrix marks as authored. An exclusion has
 * NONE: it publishes nothing at all, so its column stands with hollow cells,
 * which is the honest reading of a declared gap.
 */
export function buildOverrideViews(input: {
  ctx: StateContext
  statements: StatementGroup[]
  /** The Override bucket's notes IN FORCE, address-keyed, from the read model. */
  notes: Record<string, PersistedOverrideNote>
  /** The statement's app-wide `type · verdict` title, when the caller knows it. */
  label?: (statementId: string) => string
}): OverrideView[] {
  const { ctx } = input
  const statusOf = new Map<string, string>()
  for (const group of input.statements)
    for (const g of group.groups)
      if (g.statementId) statusOf.set(g.statementId, g.statementStatus)

  const out: OverrideView[] = []
  /**
   * THE NOTE AT ONE ADDRESS, from the PERSISTED store.
   *
   * `snapshot: null` on every one of them, and that is the truth rather than a
   * shortcut: the store holds the note, the author and the timestamp, and it does
   * NOT hold the facts the note was written against. `overrideDiverged` reads a
   * null snapshot as "cannot diverge" — the same grandfathering idiom a verify mark
   * with no confirmation record uses — so no override is ever marked as needing a
   * second look on evidence nobody kept. That is the honest gap, and it is named in
   * one place instead of being papered over by re-deriving a snapshot at read time
   * from facts that have already moved.
   */
  const noteAt = (address: string): OverrideNote | null => {
    const row = input.notes[address]
    if (!row) return null
    return {
      kind: row.kind as OverrideNote["kind"],
      comment: row.comment,
      createdAt: row.created_at ?? "",
      snapshot: null,
      author: row.author ?? null,
    }
  }

  for (const s of ctx.statements) {
    const key = scopeKey(s.framework, s.groupIndex)
    const res = ctx.scopeResolutions[key]
    const unmatched = res?.unresolved_refs ?? []
    const base = {
      unit: "statement" as const,
      unitId: s.id,
      statementId: s.id,
      framework: s.framework,
      groupIndex: s.groupIndex,
      label: input.label?.(s.id),
    }
    const cells = s.selectedArticles.map((a) => `${s.framework}:${a}`)
    // The STORE addresses by position (`statement:<fw>:<gi>`), which is what
    // survives a re-extraction; the flow's own key is the staged row's id.
    const stmtNote = noteAt(statementStoreAddress(s.framework, s.groupIndex))

    // excluded — the staged statement is rejected. A real, persisted store: it
    // takes the statement out of what approve writes to master data.
    if (statusOf.get(s.id) === "rejected") {
      out.push({
        ...base,
        itemId: "",
        kind: "excluded",
        comment: stmtNote?.comment ?? null,
        at: stmtNote?.createdAt ?? null,
        diverged: overrideDiverged(stmtNote, null),
        cells: [],
      })
      continue
    }
    // suppressed — applied with references nothing covers still outstanding.
    if (ctx.applied[key] === true && unmatched.length > 0) {
      out.push({
        ...base,
        itemId: "",
        kind: "suppressed",
        comment: stmtNote?.comment ?? null,
        at: stmtNote?.createdAt ?? null,
        diverged: overrideDiverged(stmtNote, null),
        cells,
      })
      continue
    }
    // proceed_unknown — note-driven, and in force only while the verdict still
    // reads unknown. It retires by itself the moment a real verdict lands.
    // proceed_unknown — MEMBERSHIP IS THE WIRE'S. `GET /{id}/statements` derives it
    // (an active note of that kind AND a verdict still `unknown`) and both twins
    // read that one answer, so the bucket joins the note to it rather than
    // re-deriving the guard a second way and risking a different result.
    if (s.proceedUnknown && stmtNote?.kind === "proceed_unknown") {
      out.push({
        ...base,
        itemId: "",
        kind: "proceed_unknown",
        comment: stmtNote.comment,
        at: stmtNote.createdAt || null,
        diverged: overrideDiverged(stmtNote, null),
        cells,
      })
    }
  }

  // ignored — one article of one statement, in force while it is still in scope.
  for (const s of ctx.statements) {
    for (const art of s.selectedArticles) {
      const note = noteAt(cellStoreAddress(s.framework, art))
      if (note?.kind !== "ignored") continue
      const cell = `${s.framework}:${art}`
      out.push({
        unit: "statement",
        unitId: s.id,
        itemId: art,
        kind: "ignored",
        statementId: s.id,
        framework: s.framework,
        groupIndex: s.groupIndex,
        articleId: art,
        label: input.label?.(s.id),
        comment: note.comment,
        at: note.createdAt,
        diverged: overrideDiverged(note, null),
        cells: [cell],
      })
    }
  }
  return out
}

/**
 * Collision items for run-wide statement-title numbering. `published` (the list
 * version's effective date — a CMS fact) is not on the wire yet, so it is null
 * and collisions fall back to groupIndex order (exact recency numbering is a
 * later refinement once list catalogs are exposed to the flow).
 */
export function buildCollisionItems(
  statements: StatementGroup[],
): CollisionItem[] {
  const items: CollisionItem[] = []
  for (const group of statements) {
    group.groups.forEach((g, gi) => {
      if (!g.statementId) return
      items.push({
        id: g.statementId,
        slug: group.frameworkSlug,
        verdict: asVerdict(g.verdictOverride ?? g.values.verdict),
        listVersion: g.values.listVersion,
        groupIndex: gi,
        published: null,
      })
    })
  }
  return items
}

/**
 * THE HALF-WRITTEN DISPOSITION, SAID OUT LOUD (issue #1932).
 *
 * Two of the kinds are a note PLUS a paired fact, and the note is not the half that
 * takes effect:
 *
 *   excluded    the note says why; the staged row's `rejected` status is what
 *               actually keeps the statement out of what approve writes.
 *   suppressed  the note says why; an APPLIED scope with references still
 *               outstanding is what is being suppressed.
 *
 * When the note stands and its fact does not, `buildOverrideViews` emits no view at
 * all — so the disposition is not in force, the statement's work stays open, and
 * NOTHING ON SCREEN SAYS SO. That silence is what made the exclude gesture read as
 * doing nothing: the reviewer recorded a reason, saw no change, and recorded it
 * again. Eighteen times across three declarations on dev, and every one of those
 * re-records silently superseded the reason before it.
 *
 * THE SERVER ALREADY DIAGNOSES THIS and it cannot reach the reader. `apply_overrides`
 * builds the same list and `approve` returns it as `override_mismatches` — but
 * `canFinish` requires an empty worklist, and in this exact state the browser holds
 * the statement's issues too, so approve is never called and the message never
 * leaves the server. Hence a client-side derivation rather than plumbing that 409:
 * the browser holds both halves already (`readModel.overrides` and each statement's
 * staged status), so it can say it at the card where the gesture happened.
 *
 * Kept OUT of `buildOverrideViews` deliberately: that function answers "what is in
 * force", and these are precisely the notes that are not. Mirror of the backend's
 * `_PAIRED_FACT` in `mc_declaration_overrides.py` — keep the two in step.
 */
export interface OverrideMismatch {
  /** The store address the note stands at. */
  address: string
  kind: "excluded" | "suppressed"
  statementId: string
  framework: string
  groupIndex: number
}

export function overrideMismatches(input: {
  ctx: StateContext
  statements: StatementGroup[]
  notes: Record<string, PersistedOverrideNote>
}): OverrideMismatch[] {
  const { ctx } = input
  const statusOf = new Map<string, string>()
  for (const group of input.statements)
    for (const g of group.groups)
      if (g.statementId) statusOf.set(g.statementId, g.statementStatus)

  const out: OverrideMismatch[] = []
  for (const s of ctx.statements) {
    const key = scopeKey(s.framework, s.groupIndex)
    const address = statementStoreAddress(s.framework, s.groupIndex)
    const kind = input.notes[address]?.kind
    if (kind !== "excluded" && kind !== "suppressed") continue
    const rejected = statusOf.get(s.id) === "rejected"
    // A rejected statement satisfies a `suppressed` note too: the item is closed for
    // a STRONGER reason, so there is no divergence to report. Same rule as the twin.
    const held =
      kind === "excluded"
        ? rejected
        : rejected ||
          (ctx.applied[key] === true &&
            (ctx.scopeResolutions[key]?.unresolved_refs ?? []).length > 0)
    if (held) continue
    out.push({
      address,
      kind,
      statementId: s.id,
      framework: s.framework,
      groupIndex: s.groupIndex,
    })
  }
  return out
}

export interface OpenWork {
  ctx: StateContext
  overrides: OverrideView[]
  /** The worklist before the override layer. */
  rawWorklist: WorklistIssue[]
  /** What is still open: the worklist after the override layer. */
  worklist: WorklistIssue[]
  overrideMismatches: OverrideMismatch[]
}

/**
 * The open review work from the four reads alone — the pipeline the review screens
 * run (`useDeclarationFlowState`), pure so the server's twin (`derive_open_work` in
 * `mc_declaration_review_service.py`) can be pinned to it by the shared `flowParity`
 * fixture. The notes in force are the read model's `overrides`; which frameworks have
 * a catalogue is the read model's `catalog_frameworks`.
 */
export function deriveOpenWork(input: {
  statements: StatementGroup[]
  readModel: DeclarationReadModel
  articleStates: ArticleState[]
  conditionRows: ConditionFieldRow[]
  pendingIssues?: Record<string, string[]>
  /** The statement titles for the override views, given the built context. */
  label?: (ctx: StateContext) => (statementId: string) => string
}): OpenWork {
  const catalogSet = new Set(input.readModel.catalog_frameworks ?? [])
  const ctx = buildStateContext({
    statements: input.statements,
    readModel: input.readModel,
    articleStates: input.articleStates,
    conditionRows: input.conditionRows,
    frameworkHasCatalog: (fw) => catalogSet.has(fw),
    pendingIssues: input.pendingIssues,
  })
  const notes = input.readModel.overrides ?? {}
  const overrides = buildOverrideViews({
    ctx,
    statements: input.statements,
    notes,
    label: input.label?.(ctx),
  })
  const rawWorklist = deriveWorklist(ctx)
  return {
    ctx,
    overrides,
    rawWorklist,
    worklist: applyOverrides(rawWorklist, overrides),
    overrideMismatches: overrideMismatches({
      ctx,
      statements: input.statements,
      notes,
    }),
  }
}

/**
 * `statementId → the kind whose paired fact is missing`, for the card that has to say
 * so. Shared rather than derived at each render site: both statement surfaces need the
 * same lookup, and two byte-identical Maps are exactly the drift this repo's
 * no-verbatim-duplicates rule is about.
 *
 * Lives here beside its producer rather than next to `overrideForStatement` in
 * `overrides.ts`: that module is the pure model this one imports FROM, so a reader of
 * `OverrideMismatch` cannot live there without inverting the dependency.
 */
export function halfWrittenBy(
  mismatches: OverrideMismatch[],
): Map<string, OverrideMismatch["kind"]> {
  return new Map(mismatches.map((m) => [m.statementId, m.kind]))
}
